import { Dispatch } from 'react'
import * as subscriptionApi from '../services/api'
import { ApiResponse } from '../../types'
import { Subscription, SubscriptionAccount } from './store'
import { AppState } from '../../../global/store'
import { ActionError } from '../../../global/errors'
import * as searchApi from '../../../global/nav/services/api'
import * as accountsApi from '../../accounts/services/api'
import { RequestState } from '../../types'

import {
  LOAD_SUBSCRIPTIONS_REQUEST,
  LOAD_SUBSCRIPTIONS_RECEIVED,
  LOAD_SUBSCRIPTIONS_ERROR,
  LOAD_SUBSCRIPTION_DETAILS_REQUEST,
  LOAD_SUBSCRIPTION_DETAILS_RECEIVED,
  LOAD_SUBSCRIPTION_DETAILS_ERROR,
  LOAD_SINGLE_SUBSCRIPTION_REQUEST,
  LOAD_SINGLE_SUBSCRIPTION_RECEIVED,
  LOAD_SINGLE_SUBSCRIPTION_ERROR,
  SET_SUBSCRIPTION_EXPANDED,
  SET_FILTER_PANEL_OPEN,
  LOAD_FILTERS_REQUEST,
  LOAD_FILTERS_RECEIVED,
  LOAD_FILTERS_ERROR,
  LOAD_SUBSCRIPTION_ACCOUNT_FILTERS_REQUEST,
  LOAD_SUBSCRIPTION_ACCOUNT_FILTERS_RECEIVED,
  LOAD_SUBSCRIPTION_ACCOUNT_FILTERS_ERROR
} from './reducers'

import { SubscriptionsAction, FiltersAction } from './reducers'
import moment from 'moment'

export function loadSubscriptions(page: number, partnerId: string, sort: string) {
  return async function (state: AppState, dispatch: Dispatch<SubscriptionsAction>) {
    try {
      dispatch({
        type: LOAD_SUBSCRIPTIONS_REQUEST,
        page
      })

      const subscriptionsPerPage = 25
      const subscriptions: ApiResponse = await subscriptionApi.getSubscriptions(partnerId, subscriptionsPerPage, page, sort)

      const mappedSubscriptions: Subscription[] = subscriptions.data.map((subscription: ApiResponse) => {
        return mapSubscription(subscription, false)
      })

      const totalPages = subscriptions.meta.pagination.total_pages
      const totalCount = subscriptions.meta.pagination.total

      dispatch({
        type: LOAD_SUBSCRIPTIONS_RECEIVED,
        page,
        subscriptions: mappedSubscriptions,
        totalPages,
        totalCount
      })
    } catch (error) {
      throw new ActionError(LOAD_SUBSCRIPTIONS_ERROR, error as Error)
    }
  }
}

export function loadAccountSubscription(accountId: string) {
  return async function (state: AppState, dispatch: Dispatch<SubscriptionsAction>) {
    try {
      dispatch({
        type: LOAD_SINGLE_SUBSCRIPTION_REQUEST
      })

      const account = await searchApi.getAccount(accountId)
      const subscription = account.data.subscription
      const details = await subscriptionApi.getSubscriptionDetails(subscription.data.id)
      const accounts = details.data.accounts.data

      const mappedAccounts: SubscriptionAccount[] =
        accounts.map((account: ApiResponse) => {
          return mapAccount(account)
        })
        .sort(compareAccounts)

      const mappedSubscription = mapSubscription(details.data, true)
      mappedSubscription.accounts = mappedAccounts

      dispatch({
        type: LOAD_SINGLE_SUBSCRIPTION_RECEIVED,
        subscription: mappedSubscription,
        searchedForAccount: account.data
      })
    } catch {
      dispatch({
        type: LOAD_SINGLE_SUBSCRIPTION_ERROR
      })
    }
  }
}

export function loadSingleSubscription(subscriptionId: string) {
  return async function (state: AppState, dispatch: Dispatch<SubscriptionsAction>) {
    try {
      dispatch({
        type: LOAD_SINGLE_SUBSCRIPTION_REQUEST
      })

      const subscription = await subscriptionApi.getSubscriptionDetails(subscriptionId)
      const accounts = subscription.data.accounts.data

      const mappedAccounts: SubscriptionAccount[] = accounts.map((account: ApiResponse) => {
        return mapAccount(account)
      }).sort(compareAccounts)

      const mappedSubscription = mapSubscription(subscription.data, true)
      mappedSubscription.accounts = mappedAccounts

      dispatch({
        type: LOAD_SINGLE_SUBSCRIPTION_RECEIVED,
        subscription: mappedSubscription
      })
    } catch (error) {
      dispatch({
        type: LOAD_SINGLE_SUBSCRIPTION_ERROR
      })
    }
  }
}

export function loadSubscriptionDetails(subscriptionId: string) {
  return async function (_state: AppState, dispatch: Dispatch<SubscriptionsAction>) {
    try {
      dispatch({
        type: LOAD_SUBSCRIPTION_DETAILS_REQUEST,
        subscriptionId
      })

      const details: ApiResponse = await subscriptionApi.getSubscriptionDetails(subscriptionId)
      const accounts = details.data.accounts.data

      const mappedAccounts: SubscriptionAccount[] = accounts.map((account: ApiResponse) => {
        return mapAccount(account)
      }).sort(compareAccounts)

      dispatch({
        type: LOAD_SUBSCRIPTION_DETAILS_RECEIVED,
        accounts: mappedAccounts,
        subscriptionId
      })
    } catch (error) {
      throw new ActionError(LOAD_SUBSCRIPTION_DETAILS_ERROR, error as Error)
    }
  }
}

export function loadFilteredSubscriptions(filterId: string, page: number, partnerId: string, sort: string) {
  return async function (_state: AppState, dispatch: Dispatch<SubscriptionsAction>) {
    try {
      dispatch({
        type: LOAD_SUBSCRIPTIONS_REQUEST,
        page
      })

      const subscriptionsPerPage = 25
      const subscriptions: ApiResponse = await subscriptionApi.getFilteredSubscriptions(
        partnerId,
        filterId,
        subscriptionsPerPage,
        page,
        sort
      )

      const mappedSubscriptions: Subscription[] = subscriptions.data.subscriptions.data.map(
        (subscription: ApiResponse) => {
          return mapSubscription(subscription, false)
        })

      const totalPages = subscriptions.data.subscriptions.meta.pagination.total_pages
      const totalCount = subscriptions.data.subscriptions.meta.pagination.total

      dispatch({
        type: LOAD_SUBSCRIPTIONS_RECEIVED,
        page,
        subscriptions: mappedSubscriptions,
        totalPages,
        totalCount
      })
    } catch (error) {
      throw new ActionError(LOAD_SUBSCRIPTIONS_ERROR, error as Error)
    }
  }
}

export function setFilterPanelOpen(open: boolean) {
  return async function (_state: AppState, dispatch: Dispatch<SubscriptionsAction>) {
    dispatch({
      type: SET_FILTER_PANEL_OPEN,
      filterPanelOpen: open
    })
  }
}

export function setSubscriptionExpanded(subscriptionId: string, expanded: boolean) {
  return async function (state: AppState, dispatch: Dispatch<SubscriptionsAction>) {
    dispatch({
      type: SET_SUBSCRIPTION_EXPANDED,
      subscriptionId,
      expanded
    })
  }
}

export function loadFilters(partnerId: string) {
  return async function (state: AppState, dispatch: Dispatch<FiltersAction>) {
    try {
      dispatch({
        type: LOAD_FILTERS_REQUEST
      })

      const filters = await subscriptionApi.getFilters(partnerId)

      dispatch({
        type: LOAD_FILTERS_RECEIVED,
        filters: filters.data
      })
    } catch (error) {
      throw new ActionError(LOAD_FILTERS_ERROR, error as Error)
    }
  }
}

export function searchSubscriptions(partnerId: string, text: string, includeDeactivated: boolean, page: number, sort: string) {
  return async function (state: AppState, dispatch: Dispatch<SubscriptionsAction>) {
    try {
      dispatch({
        type: LOAD_SUBSCRIPTIONS_REQUEST,
        page
      })

      const safeString = encodeURIComponent(text.trim())
      const subscriptions = await searchApi.getPagedSubscriptionSearchResults(partnerId, safeString, includeDeactivated, page, sort)

      const mappedSubscriptions: Subscription[] = subscriptions.data.map((subscription: ApiResponse) => {
        return mapSubscription(subscription, false)
      })

      const totalPages = subscriptions.meta.pagination.total_pages
      const totalCount = subscriptions.meta.pagination.total

      dispatch({
        type: LOAD_SUBSCRIPTIONS_RECEIVED,
        page,
        subscriptions: mappedSubscriptions,
        totalPages,
        totalCount
      })
    } catch (error) {
      dispatch({
        type: LOAD_SUBSCRIPTIONS_ERROR,
        error: error as Error
      })
    }
  }
}

export function loadSubscriptionAccountFilters(subscriptionId: string, accountId: string) {
  return async function (state: AppState, dispatch: Dispatch<SubscriptionsAction>) {
    try {
      dispatch({
        type: LOAD_SUBSCRIPTION_ACCOUNT_FILTERS_REQUEST,
        subscriptionId,
        accountId
      })

      const filters = await accountsApi.getFiltersForAccount(accountId)
      const mappedFilters = filters.data.map((filter: ApiResponse) => {
        return mapFilter(filter)
      })

      dispatch({
        type: LOAD_SUBSCRIPTION_ACCOUNT_FILTERS_RECEIVED,
        subscriptionId,
        accountId,
        filters: mappedFilters
      })
    } catch (error) {
      throw new ActionError(LOAD_SUBSCRIPTION_ACCOUNT_FILTERS_ERROR, error as Error)
    }
  }
}

function mapSubscription(data: ApiResponse, isExpanded: boolean) {
  return {
    id: data.id,
    name: data.name,
    type: data.type,
    active: data.deactivated_at === null || data.deactivated_at === undefined,
    renewal:data.renewal_date ?  moment(data.renewal_date).toDate() : undefined,
    members: data.stats.data.members,
    memberAllowance: data.members,
    records: data.stats.data.records,
    freeRecords: data.stats.free_records,
    recordAllowance: data.records,
    requestState: RequestState.NotLoaded,
    isExpanded,
    invitations: data.stats.data.invitations,
    package: data.package.data.name,
    accounts: [] as SubscriptionAccount[]
  }
}

function mapAccount(data: ApiResponse) {
  return {
    id: data.id,
    name: data.name,
    records: data.stats.data.records,
    freeRecords: data.stats.data.free_records,
    isDemo: data.demo,
    active: data.active,
    lastActive: data.latest_activity ? moment(data.latest_activity.data.created_at).toDate() : undefined,
    requestState: RequestState.NotLoaded
  }
}

function mapFilter(data: ApiResponse) {
  return {
    id: data.id,
    key: data.key,
    name: data.name,
    profile: data.profile,
    count: data.count
  }
}

function compareAccounts(account1: SubscriptionAccount, account2: SubscriptionAccount) {
  if (account1.active && !account2.active) {
    return -1
  }

  if (account1.active && !account2.active) {
    return 1
  }

  if (account1.active === account2.active) {
    return account1.name.localeCompare(account2.name)
  }
}
